import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import type { Quotation, Party, SalesRep, ItemCode } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../../context/CompanyContext';
import { generateQuotationPDF } from '../../lib/pdfGenerator';
import { generateQuotationExcel } from '../../lib/excelGenerator';
import { calculateFinancials, calculateLineItem, type LineItemInput } from '../../lib/pricing';
import { logUserActivity } from '../../lib/activityLogger';
import ItemCodeBuilder from '../../components/ItemCodeBuilder';

interface EditQuotationProps {
  quotationId: number;
  onBack: () => void;
}

export default function EditQuotationPage({ quotationId, onBack }: EditQuotationProps) {
  const { profile, isAdmin, isExecutive } = useAuth();
  const maxDiscountAuthority = profile?.max_discount_limit ?? (isAdmin || isExecutive ? 100 : 10);
  const { companies } = useCompany();
  const [parties, setParties] = useState<Party[]>([]);
  const [reps, setReps] = useState<SalesRep[]>([]);
  const [catalogItems, setCatalogItems] = useState<ItemCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [originalQuote, setOriginalQuote] = useState<Quotation | null>(null);

  const [form, setForm] = useState({
    quotation_number: '',
    quotation_date: '',
    company_id: '',
    rep_id: '',
    party_id: '',
    query_number: '',
    query_date: '',
    kind_attn: '',
    status: 'draft' as Quotation['status'],
    global_discount_percent: 0,
    pf_percent: 0,
    others: 0,
    gst_percent: 18,
    prices_terms: 'EX-WORKS',
    payment_terms: 'AGAINST PI',
    dispatch_terms: '10-12 DAYS',
    freight_terms: 'AT YOUR COST',
    validity_terms: '30 DAYS',
    insurance_terms: 'AT YOUR COST',
    remarks: '',
  });

  const [items, setItems] = useState<LineItemInput[]>([]);
  const [currentItem, setCurrentItem] = useState<LineItemInput>({
    sno: 1,
    item_code: '',
    description: '',
    quantity: 1,
    rate: 0,
    discount_percent: 0,
  });
  const [showCodeBuilder, setShowCodeBuilder] = useState(false);
  const [discountMode, setDiscountMode] = useState<'all' | 'separate'>('all');

  const loadQuotation = useCallback(async () => {
    setLoading(true);
    const [qRes, iRes, pRes, rRes, catRes] = await Promise.all([
      supabase.from('quotations').select('*').eq('id', quotationId).single(),
      supabase.from('quotation_items').select('*').eq('quotation_id', quotationId).order('sno'),
      supabase.from('parties').select('*').order('name'),
      supabase.from('sales_reps').select('*').order('name'),
      supabase.from('item_codes').select('*').order('code'),
    ]);

    if (qRes.data) {
      const q = qRes.data;
      setOriginalQuote(q);
      setForm({
        quotation_number: q.quotation_number,
        quotation_date: q.quotation_date,
        company_id: String(q.company_id),
        rep_id: String(q.rep_id),
        party_id: String(q.party_id),
        query_number: q.query_number || '',
        query_date: q.query_date || '',
        kind_attn: q.kind_attn || '',
        status: q.status || 'draft',
        global_discount_percent: q.discount_percent || 0,
        pf_percent: q.pf_percent || 0,
        others: q.others || 0,
        gst_percent: q.gst_percent || 18,
        prices_terms: q.prices_terms || 'EX-WORKS',
        payment_terms: q.payment_terms || 'AGAINST PI',
        dispatch_terms: q.dispatch_terms || '10-12 DAYS',
        freight_terms: q.freight_terms || 'AT YOUR COST',
        validity_terms: q.validity_terms || '30 DAYS',
        insurance_terms: q.insurance_terms || 'AT YOUR COST',
        remarks: q.remarks || '',
      });
    }

    if (iRes.data && iRes.data.length > 0) {
      setItems(iRes.data.map((r: any) => ({
        sno: r.sno,
        item_code: r.item_code,
        description: r.description || '',
        quantity: r.quantity,
        rate: r.rate,
        discount_percent: r.discount_percent || 0,
      })));
    }

    setParties(pRes.data || []);
    setReps(rRes.data || []);
    setCatalogItems(catRes.data || []);
    setLoading(false);
  }, [quotationId]);

  useEffect(() => {
    loadQuotation();
  }, [loadQuotation]);

  function handleProductSelect(itemCodeValue: string) {
    const found = catalogItems.find(i => i.code === itemCodeValue);
    if (found) {
      setCurrentItem({
        ...currentItem,
        item_code: found.code,
        description: found.description || found.code,
        rate: found.base_price || currentItem.rate || 0,
      });
    } else {
      setCurrentItem({ ...currentItem, item_code: itemCodeValue });
    }
  }

  function addItem() {
    if (!currentItem.item_code) return;
    setItems([...items, { ...currentItem, sno: items.length + 1 }]);
    setCurrentItem({ sno: items.length + 2, item_code: '', description: '', quantity: 1, rate: 0, discount_percent: 0 });
  }

  function removeItem(index: number) {
    setItems(items.filter((_, i) => i !== index).map((it, idx) => ({ ...it, sno: idx + 1 })));
  }

  function handleCodeBuilderAdd(code: string, desc: string, rate: number) {
    setItems([...items, { sno: items.length + 1, item_code: code, description: desc, quantity: 1, rate, discount_percent: 0 }]);
  }

  // In DiscSep mode, ignore global discount and use per-item discounts
  const effectiveGlobalDiscount = discountMode === 'separate' ? 0 : form.global_discount_percent;
  const financials = calculateFinancials(items, effectiveGlobalDiscount, form.pf_percent, form.others, form.gst_percent);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!form.company_id || !form.rep_id || !form.party_id) {
      alert('Please select Company, Sales Rep, and Party.');
      return;
    }
    setSaving(true);
    const cleanQueryDate = form.query_date && form.query_date.length === 10 && !form.query_date.startsWith('000')
      ? form.query_date
      : null;

    const quotationData = {
      quotation_number: form.quotation_number,
      quotation_date: form.quotation_date,
      company_id: parseInt(form.company_id),
      rep_id: parseInt(form.rep_id),
      party_id: parseInt(form.party_id),
      query_number: form.query_number?.trim() || null,
      query_date: cleanQueryDate,
      kind_attn: form.kind_attn?.trim() || null,
      subtotal: financials.subtotal,
      discount_percent: financials.discountPercent,
      discount_amount: financials.discountAmount,
      pf_percent: financials.pfPercent,
      pf_amount: financials.pfAmount,
      others: financials.others,
      gst_percent: financials.gstPercent,
      gst_amount: financials.gstAmount,
      total: financials.total,
      amount_in_words: financials.amountInWords,
      prices_terms: form.prices_terms,
      payment_terms: form.payment_terms,
      dispatch_terms: form.dispatch_terms,
      freight_terms: form.freight_terms,
      validity_terms: form.validity_terms,
      insurance_terms: form.insurance_terms,
      remarks: form.remarks || financials.remarks || null,
      status: form.status,
      updated_at: new Date().toISOString(),
    };

    const formatDbItem = (it: LineItemInput, quoteId: number, idx: number) => {
      const calc = calculateLineItem(it);
      return {
        quotation_id: quoteId,
        sno: it.sno ?? (idx + 1),
        item_code: it.item_code,
        description: it.description || '',
        quantity: Number(it.quantity) || 1,
        unit: (it as any).unit || 'Pcs',
        rate: Number(it.rate) || 0,
        amount: Number(calc.amount) || 0,
        discount_percent: Number(calc.discount_percent) || 0,
        discount_amount: Number(calc.discount_amount) || 0,
        final_amount: Number(calc.final_amount) || 0,
      };
    };

    const { error: updateErr } = await supabase.from('quotations').update(quotationData).eq('id', quotationId);
    if (updateErr) {
      setSaving(false);
      alert('Failed to update quotation: ' + updateErr.message);
      return;
    }

    const { error: delErr } = await supabase.from('quotation_items').delete().eq('quotation_id', quotationId);
    if (delErr) {
      console.error('Error clearing old quotation items:', delErr);
    }

    if (items.length > 0) {
      const processed = items.map((it, idx) => formatDbItem(it, quotationId, idx));
      const { error: insertErr } = await supabase.from('quotation_items').insert(processed);
      if (insertErr) {
        setSaving(false);
        alert('Warning: Quotation details updated, but line items failed to save: ' + insertErr.message);
        return;
      }
    }

    setSaving(false);
    setSaved(true);
    logUserActivity({
      action_type: 'QUOTATION_UPDATED',
      resource_id: form.quotation_number,
      details: `Updated quotation ${form.quotation_number} (₹${financials.total})`,
    });
    setTimeout(() => setSaved(false), 3000);
  }

  async function handleDownloadPDF() {
    const company = companies.find(c => c.id === parseInt(form.company_id)) || companies[0];
    const party = parties.find(p => p.id === parseInt(form.party_id));
    const rep = reps.find(r => r.id === parseInt(form.rep_id));
    if (!company || !party) return;
    const q: Quotation = { ...originalQuote, ...form, company_id: parseInt(form.company_id), rep_id: parseInt(form.rep_id), party_id: parseInt(form.party_id), subtotal: financials.subtotal, total: financials.total, amount_in_words: financials.amountInWords } as Quotation;
    const pdf = generateQuotationPDF(q, company, party, items.map(it => ({ ...it, description: it.description || '', amount: it.quantity * it.rate, final_amount: calculateLineItem(it).final_amount })), rep?.name);
    pdf.save(`${form.quotation_number.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`);
    logUserActivity({
      action_type: 'PDF_GENERATED',
      resource_id: form.quotation_number,
      details: `Downloaded PDF for revised quotation ${form.quotation_number}`,
    });
  }

  async function handleDownloadExcel() {
    const company = companies.find(c => c.id === parseInt(form.company_id)) || companies[0];
    const party = parties.find(p => p.id === parseInt(form.party_id));
    const rep = reps.find(r => r.id === parseInt(form.rep_id));
    if (!company || !party) return;
    const q: Quotation = { ...originalQuote, ...form, company_id: parseInt(form.company_id), rep_id: parseInt(form.rep_id), party_id: parseInt(form.party_id), subtotal: financials.subtotal, total: financials.total, amount_in_words: financials.amountInWords } as Quotation;
    await generateQuotationExcel(q, company, party, items.map(it => ({ ...it, description: it.description || '', amount: it.quantity * it.rate, final_amount: calculateLineItem(it).final_amount })), rep?.name);
    logUserActivity({
      action_type: 'EXCEL_GENERATED',
      resource_id: form.quotation_number,
      details: `Downloaded Excel for revised quotation ${form.quotation_number}`,
    });
  }

  const currentParty = parties.find(p => p.id === parseInt(form.party_id));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mr-3" />
        <span className="text-sm text-gray-500">Loading quotation...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Navigation Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-800 transition-colors cursor-pointer"
          >
            <span>←</span>
            <span>Back to Quotations</span>
          </button>
          <span className="text-gray-300">|</span>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Edit Quotation</h1>
            <p className="text-xs text-gray-500 mt-0.5">
              <span className="font-mono font-bold text-blue-700">{form.quotation_number}</span>
              {' '}— {currentParty?.name || 'Loading...'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleDownloadPDF}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-600/20 transition-all cursor-pointer flex items-center gap-1.5"
          >
            <span>📄</span> Download PDF
          </button>
          <button
            onClick={handleDownloadExcel}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-600/20 transition-all cursor-pointer flex items-center gap-1.5"
          >
            <span>📊</span> Download Excel
          </button>
        </div>
      </div>

      {saved && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl text-xs font-bold flex items-center gap-2">
          <span>✓</span>
          <span>Quotation saved successfully! All changes are stored in the database.</span>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Header Info Grid */}
        <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-6 space-y-5">
          <div className="flex justify-between items-center border-b border-slate-100 pb-3">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Document Details</h2>
            <span className="text-[10px] font-bold px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">
              VBA RoundUp Engine Active
            </span>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Quotation Number *</label>
              <input
                className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm font-mono font-bold bg-slate-50 focus:bg-white focus:border-blue-500"
                value={form.quotation_number}
                onChange={(e) => setForm({ ...form, quotation_number: e.target.value.toUpperCase() })}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Date *</label>
              <input
                type="date"
                className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500"
                value={form.quotation_date}
                onChange={(e) => setForm({ ...form, quotation_date: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Company *</label>
              <select
                className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm font-bold text-slate-800 focus:border-blue-500"
                value={form.company_id}
                onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                required
              >
                <option value="">Select Company</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.code} - {c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Sales Rep *</label>
              <select
                className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm font-bold text-slate-800 focus:border-blue-500"
                value={form.rep_id}
                onChange={(e) => setForm({ ...form, rep_id: e.target.value })}
                required
              >
                <option value="">Select Rep</option>
                {reps.map(r => (
                  <option key={r.id} value={r.id}>{r.code} - {r.name}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-slate-700 mb-1">Customer Party *</label>
              <select
                className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500"
                value={form.party_id}
                onChange={(e) => setForm({ ...form, party_id: e.target.value })}
                required
              >
                <option value="">Select Customer</option>
                {parties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Query Number</label>
              <input
                className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500"
                value={form.query_number}
                onChange={(e) => setForm({ ...form, query_number: e.target.value })}
                placeholder="e.g. QRY-2026-001"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Query Date</label>
              <input
                type="date"
                className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500"
                value={form.query_date}
                onChange={(e) => setForm({ ...form, query_date: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Status</label>
              <select
                className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as Quotation['status'] })}
              >
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-6 space-y-4">
          <div className="flex justify-between items-center border-b border-slate-100 pb-3">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Line Items</h2>
            <span className="text-xs text-slate-500">{items.length} item{items.length !== 1 ? 's' : ''} added</span>
          </div>

          {/* Visual Code Builder Toggle */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowCodeBuilder(!showCodeBuilder)}
              className={`px-4 py-2 rounded-xl text-xs font-bold border-2 transition-all cursor-pointer ${
                showCodeBuilder
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
              }`}
            >
              ⚙️ {showCodeBuilder ? 'Hide' : 'Open'} Code Builder
            </button>
            <button
              type="button"
              onClick={() => setShowCodeBuilder(false)}
              className="px-4 py-2 rounded-xl text-xs font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer"
            >
              ✏️ Manual Entry
            </button>
          </div>

          {/* Item Code Builder (VBA Form Castors) */}
          {showCodeBuilder && (
            <div className="animate-slideUp">
              <ItemCodeBuilder
                onAddItem={handleCodeBuilderAdd}
                onClose={() => setShowCodeBuilder(false)}
              />
            </div>
          )}

          {/* Manual Add Item Form */}
          {!showCodeBuilder && (
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 grid grid-cols-1 gap-3 sm:grid-cols-12">
            <div className="sm:col-span-3">
              <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase">Item Code</label>
              <input
                type="text"
                list="edit-item-codes"
                className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm font-mono focus:border-blue-500 bg-white"
                value={currentItem.item_code}
                onChange={(e) => handleProductSelect(e.target.value)}
                placeholder="e.g. SF1/SW/PP/F/615..."
              />
              <datalist id="edit-item-codes">
                {catalogItems.slice(0, 50).map(it => (
                  <option key={it.id} value={it.code}>{it.description}</option>
                ))}
              </datalist>
            </div>
            <div className="sm:col-span-4">
              <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase">Description</label>
              <input
                className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500 bg-white"
                value={currentItem.description}
                onChange={(e) => setCurrentItem({ ...currentItem, description: e.target.value })}
                placeholder="Assembly description"
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase">Qty</label>
              <input
                type="number" min="1"
                className="block w-full px-2 py-2 border border-slate-300 rounded-xl text-sm text-center focus:border-blue-500 bg-white"
                value={currentItem.quantity}
                onChange={(e) => setCurrentItem({ ...currentItem, quantity: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase">Rate (₹)</label>
              <input
                type="number" step="0.01"
                className="block w-full px-2 py-2 border border-slate-300 rounded-xl text-sm text-right focus:border-blue-500 bg-white"
                value={currentItem.rate}
                onChange={(e) => setCurrentItem({ ...currentItem, rate: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="sm:col-span-1">
              <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase">Disc %</label>
              <input
                type="number" step="0.01"
                className="block w-full px-2 py-2 border border-slate-300 rounded-xl text-sm text-center focus:border-blue-500 bg-white"
                value={currentItem.discount_percent}
                onChange={(e) => setCurrentItem({ ...currentItem, discount_percent: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="sm:col-span-1 flex items-end">
              <button
                type="button" onClick={addItem}
                disabled={!currentItem.item_code}
                className="w-full bg-blue-600 text-white px-3 py-2 rounded-xl hover:bg-blue-700 disabled:opacity-40 text-sm font-bold transition-colors cursor-pointer"
              >+ Add</button>
            </div>
          </div>
          )}

          {/* Items Table */}
          {items.length > 0 && (
            <div className="overflow-x-auto border border-slate-200 rounded-xl">
              <table className="min-w-full divide-y divide-slate-200 text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2.5 text-left font-bold text-slate-600">#</th>
                    <th className="px-4 py-2.5 text-left font-bold text-slate-600">Item Code & Description</th>
                    <th className="px-3 py-2.5 text-center font-bold text-slate-600">Qty</th>
                    <th className="px-3 py-2.5 text-right font-bold text-slate-600">Rate (₹)</th>
                    <th className="px-3 py-2.5 text-right font-bold text-slate-600">Amount (₹)</th>
                    <th className="px-3 py-2.5 text-center font-bold text-slate-600">Disc</th>
                    <th className="px-3 py-2.5 text-right font-bold text-slate-600">Final (₹)</th>
                    <th className="px-3 py-2.5 text-center font-bold text-slate-600"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {items.map((it, idx) => {
                    const calc = calculateLineItem(it);
                    return (
                      <tr key={idx} className="hover:bg-slate-50/70">
                        <td className="px-3 py-2 text-slate-400 text-center font-bold">{it.sno}</td>
                        <td className="px-4 py-2">
                          <span className="font-mono font-bold text-slate-900">{it.item_code}</span>
                          {it.description && <p className="text-[11px] text-slate-500 mt-0.5">{it.description}</p>}
                        </td>
                        <td className="px-3 py-2 text-center">{it.quantity} Pcs</td>
                        <td className="px-3 py-2 text-right">₹{it.rate.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right">₹{calc.amount.toFixed(2)}</td>
                        <td className="px-3 py-2 text-center">
                          {discountMode === 'separate' ? (
                            <input
                              type="number" min="0" max="100" step="0.01"
                              className="w-16 px-1.5 py-1 border border-emerald-300 rounded-lg text-xs text-center font-bold focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 bg-emerald-50"
                              value={it.discount_percent || ''}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                setItems(prev => prev.map((item, i) => i === idx ? { ...item, discount_percent: val } : item));
                              }}
                              placeholder="%"
                            />
                          ) : (
                            <span className="text-blue-600 font-bold">{it.discount_percent ? `${it.discount_percent}%` : '—'}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-extrabold text-slate-900">₹{calc.final_amount.toFixed(2)}</td>
                        <td className="px-3 py-2 text-center">
                          <button type="button" onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600 font-bold text-sm cursor-pointer">✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Financial Summary + Terms */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Terms */}
          <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Terms & Charges</h2>
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-bold text-slate-500 uppercase">Discount Mode:</span>
                <button type="button" onClick={() => setDiscountMode('all')}
                  className={`px-3 py-1 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                    discountMode === 'all' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-300 hover:border-blue-300'
                  }`}>All Items</button>
                <button type="button" onClick={() => setDiscountMode('separate')}
                  className={`px-3 py-1 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
                    discountMode === 'separate' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-300'
                  }`}>Per Item (DiscSep)</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-[10px] font-bold text-slate-600">
                  {discountMode === 'all' ? 'Global Discount %' : 'Per-Item Discount (set in table)'}
                </label>
                {discountMode === 'all' && (
                  <span className="text-[9px] font-semibold text-slate-500">
                    Authority: ≤{maxDiscountAuthority}%
                  </span>
                )}
              </div>
              {discountMode === 'all' ? (
                <>
                  <input type="number" step="0.01"
                    className={`block w-full px-3 py-2 border rounded-xl text-sm focus:border-blue-500 ${
                      form.global_discount_percent > maxDiscountAuthority ? 'border-amber-400 bg-amber-50/40 text-amber-900' : 'border-slate-300'
                    }`}
                    value={form.global_discount_percent}
                    onChange={(e) => setForm({ ...form, global_discount_percent: parseFloat(e.target.value) || 0 })}
                  />
                  {form.global_discount_percent > maxDiscountAuthority && (
                    <div className="text-[10px] text-amber-700 font-semibold mt-1 flex items-center gap-1">
                      <span>⚠️</span>
                      <span>Exceeds role limit ({maxDiscountAuthority}%). Requires manager/executive override.</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-700 font-bold">
                  ✏️ Edit discount % in the items table below
                </div>
              )}
            </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-600 mb-1">P & F % (on Subtotal)</label>
                <input type="number" step="0.01"
                  className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500"
                  value={form.pf_percent}
                  onChange={(e) => setForm({ ...form, pf_percent: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-600 mb-1">Other Charges (₹)</label>
                <input type="number" step="0.01"
                  className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500"
                  value={form.others}
                  onChange={(e) => setForm({ ...form, others: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-600 mb-1">GST %</label>
                <input type="number" step="0.01"
                  className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500"
                  value={form.gst_percent}
                  onChange={(e) => setForm({ ...form, gst_percent: parseFloat(e.target.value) || 18 })}
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-slate-600 mb-1">Remarks</label>
              <input
                className="block w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500"
                value={form.remarks || financials.remarks || ''}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                placeholder={financials.remarks || 'Terms / Remarks'}
              />
            </div>
          </div>

          {/* Live Financial Summary */}
          <div className="bg-slate-900 rounded-2xl shadow-elevated p-6 space-y-3 text-sm text-white">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-300 border-b border-slate-700 pb-3">Financial Summary (VBA Compliant)</h2>
            <div className="flex justify-between text-slate-300">
              <span>SUBTOTAL</span>
              <span className="font-bold text-white">₹{financials.subtotal.toLocaleString('en-IN')}</span>
            </div>
            {financials.discountAmount > 0 && (
              <div className="flex justify-between text-emerald-400">
                <span>Discount ({financials.discountPercent.toFixed(2)}%)</span>
                <span>- ₹{financials.discountAmount.toLocaleString('en-IN')}</span>
              </div>
            )}
            {financials.pfAmount > 0 && (
              <div className="flex justify-between text-slate-300">
                <span>P & F ({financials.pfPercent}% on Subtotal)</span>
                <span>+ ₹{financials.pfAmount.toLocaleString('en-IN')}</span>
              </div>
            )}
            {financials.others > 0 && (
              <div className="flex justify-between text-slate-300">
                <span>Others</span>
                <span>+ ₹{financials.others.toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="flex justify-between text-slate-400 border-t border-slate-700 pt-2">
              <span>Taxable Base</span>
              <span>₹{financials.taxableBase.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between text-slate-300">
              <span>GST ({financials.gstPercent}%)</span>
              <span>+ ₹{financials.gstAmount.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between text-xl font-black border-t-2 border-blue-500 pt-3 text-blue-400">
              <span>TOTAL</span>
              <span>₹{financials.total.toLocaleString('en-IN')}</span>
            </div>
            <p className="text-[10px] text-slate-400 italic mt-1">{financials.amountInWords}</p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onBack}
            className="px-5 py-2.5 border border-slate-300 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || items.length === 0}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-lg shadow-blue-600/25 disabled:opacity-40 transition-all cursor-pointer flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                Saving...
              </>
            ) : (
              <>💾 Save Changes</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
